import { Loader2, Star } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

interface PuzzleRating {
  id: number;
  customPuzzleId: number;
  userId: number;
  rating: number;
  comment?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: number;
    displayName?: string;
    email: string;
    avatarUrl?: string;
  };
}

interface PuzzleRatingsResponse {
  ratings: PuzzleRating[];
  averageRating: number;
  totalRatings: number;
}

interface PuzzleReviewsTabProps {
  puzzleId: number;
  ratingsData: PuzzleRatingsResponse | undefined;
  isRatingsLoading: boolean;
  onOpenRatingDialog: (puzzleId: number) => void;
}

export function PuzzleReviewsTab({ 
  puzzleId,
  ratingsData, 
  isRatingsLoading,
  onOpenRatingDialog 
}: PuzzleReviewsTabProps) {
  const { t } = useTranslation();
  
  const handleRateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenRatingDialog(puzzleId);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">{t("desafios_cacatais.reviews.title", { fallback: "Reviews" })}</h3>
        <Button 
          variant="outline" 
          size="sm" 
          className="text-xs" 
          onClick={handleRateClick}
        >
          {t("desafios_cacatais.rate_button", { fallback: "Avaliar" })}
        </Button>
      </div>
      {isRatingsLoading ? (
        <div className="text-center py-4">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : ratingsData && ratingsData.ratings.length > 0 ? (
        <div>
          <div className="mb-6 bg-zinc-800 p-4 rounded-md">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-white">{t("desafios_cacatais.reviews.average_rating", { fallback: "Average Rating" })}</h4>
              <div className="flex items-center">
                <span className="text-white font-bold mr-2">{ratingsData.averageRating.toFixed(1)}</span>
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`h-4 w-4 ${star <= Math.round(ratingsData.averageRating) ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-600'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-sm text-zinc-400">{t("desafios_cacatais.reviews.total_ratings", { count: ratingsData.totalRatings, fallback: `${ratingsData.totalRatings} ratings` })}</p>
          </div>

          <div className="space-y-4">
            {ratingsData.ratings.map((rating) => (
              <div key={rating.id} className="bg-zinc-800 p-4 rounded-md">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex items-center gap-2">
                    {rating.user.avatarUrl ? (
                      <img 
                        src={rating.user.avatarUrl} 
                        alt={rating.user.displayName || "User"} 
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-sm text-white">
                          {rating.user.displayName?.charAt(0) || rating.user.email.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <Link 
                        href={`/users/${rating.user.displayName ? rating.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : rating.user.id}`} 
                        className="text-sm font-medium text-white hover:text-blue-400 hover:underline transition-colors"
                      >
                        {rating.user.displayName || rating.user.email.split('@')[0]}
                      </Link>
                      <p className="text-xs text-zinc-500">{format(new Date(rating.createdAt), 'dd/MM/yyyy')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${star <= rating.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-600'}`}
                      />
                    ))}
                  </div>
                </div>
                {rating.comment && (
                  <p className="text-zinc-300">{rating.comment}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-10">
          <p className="text-zinc-400">{t("desafios_cacatais.reviews.no_reviews", { fallback: "Nenhuma avaliação ainda." })}</p>
        </div>
      )}
    </div>
  );
}
